import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import {
    Typography,
    Stack,
    CircularProgress,
    useTheme,
    ButtonBase,
    Box,
} from "@mui/material";
import { alpha, darken } from "@mui/material/styles";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import AttachFileRounded from "@mui/icons-material/AttachFileRounded";
import ChatBubbleOutlineRounded from "@mui/icons-material/ChatBubbleOutlineRounded";
import PersonRounded from "@mui/icons-material/PersonRounded";
import TextField from "../../TextField";
import { useLoaderData, useNavigate } from "react-router-dom";
import User from "../../../api/User";
import { useCookies } from "react-cookie";
import GalleryImage from "../../GalleryImage";
import dayjs, { Dayjs } from "dayjs";
import Message from "../../../api/Message";
import { useFilePicker } from "use-file-picker";
import {
    FileAmountLimitValidator,
    FileSizeValidator,
    FileTypeValidator,
} from "use-file-picker/validators";
import { uploadToCloudinary } from "../../../utils/cloudinaryUtils";
import { PWAUtils } from "../../../utils/PWAUtils";
import { APP_MAX_WIDTH } from "../../../constants/layout";
import { FullScreenLoader } from "../../FullScreenLoader";
import { FONTS } from "../../../constants/styles";

export const loader = async (request: any) => {
    const client_id = await request.params.client_id;
    return { client_id };
};

const PAGE_SIZE = 20;
const MAX_MESSAGE_LENGTH = 5000;

const ClientChat = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();
    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const client_id = (useLoaderData() as any).client_id as string;

    const [history, setHistory] = useState<Message[]>([]);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [message, setMessage] = useState("");

    const [user, setUser] = useState<User | undefined>();
    const [image, setImage] = useState<string | null | undefined>();

    const topMessageRef = useRef<HTMLDivElement | null>(null);
    const messagesEndRef = useRef<null | HTMLDivElement>(null);
    const isNearBottomRef = useRef(true);

    const hasScrolledToTopRef = useRef(false);
    const initialScrollDone = useRef(false);

    const isMobile = PWAUtils.isMobile();

    const chatContainerRef = useRef<HTMLDivElement | null>(null);

    const scrollToBottom = (smooth = true) => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: smooth ? "smooth" : "auto",
            });
        }
    };

    useLayoutEffect(() => {
        if (!initialScrollDone.current && history.length > 0) {
            scrollToBottom(false);
            initialScrollDone.current = true;
        }
    }, [history]);

    useEffect(() => {
        if (!uuid || !brand_id) {
            navigate("/");
        }
    }, [uuid, brand_id, navigate]);

    const loadNewMessages = useCallback(async () => {
        if (!uuid || !brand_id || !user) return;
        const res = await Message.list(uuid, brand_id, user.key, 0, PAGE_SIZE);
        const orderedMessages = res.messages.reverse();
        setHistory((prev) => {
            const withoutTemp = prev.filter((m) => !m.key.startsWith("temp-"));
            const prevKeys = new Set(withoutTemp.map((m) => m.key));
            const unique = orderedMessages.filter((m) => !prevKeys.has(m.key));
            return [...withoutTemp, ...unique];
        });
    }, [uuid, brand_id, user]);

    const loadOlderMessages = useCallback(async () => {
        if (loadingOlder || !hasMore || !user || !uuid || !brand_id) return;
        try {
            setLoadingOlder(true);

            const start = history.length;

            const scrollEl = chatContainerRef.current;
            if (!scrollEl) return;
            const prevScrollHeight = scrollEl.scrollHeight;

            const res = await Message.list(
                uuid,
                brand_id,
                user.key,
                start,
                PAGE_SIZE
            );
            const orderedMessages = res.messages.reverse();

            setHistory((prev) => {
                const prevKeys = new Set(prev.map((m) => m.key));
                const unique = orderedMessages.filter(
                    (m) => !prevKeys.has(m.key)
                );
                return [...unique, ...prev];
            });

            requestAnimationFrame(() => {
                const newScrollHeight = scrollEl.scrollHeight;
                const scrollDiff = newScrollHeight - prevScrollHeight;
                scrollEl.scrollTop = scrollEl.scrollTop + scrollDiff;

                setTimeout(() => {
                    scrollEl.scrollTo({
                        top: scrollEl.scrollTop - 50,
                        behavior: "smooth",
                    });
                }, 100);
            });

            if (
                res.messages.length < PAGE_SIZE ||
                res.messages.length + start >= res.total
            ) {
                setHasMore(false);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoadingOlder(false);
        }
    }, [loadingOlder, hasMore, history.length, user, uuid, brand_id]);

    useEffect(() => {
        const init = async () => {
            try {
                const res = await User.info(uuid, brand_id, client_id);
                setUser(res);
                setImage(res?.image);
            } catch (err) {
                console.error(err);
            }
        };
        if (uuid && brand_id && client_id) init();
    }, [uuid, brand_id, client_id]);

    useEffect(() => {
        if (!user || !uuid || !brand_id) return;
        loadNewMessages();
    }, [user, loadNewMessages, uuid, brand_id]);

    useEffect(() => {
        if (!topMessageRef.current || loadingOlder || !hasMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadOlderMessages();
                }
            },
            { rootMargin: "100px" }
        );

        if (topMessageRef.current) observer.observe(topMessageRef.current);
        return () => observer.disconnect();
    }, [history, hasMore, loadingOlder, loadOlderMessages]);

    useEffect(() => {
        const interval = setInterval(async () => {
            const res = await Message.list(
                uuid,
                brand_id,
                client_id,
                0,
                PAGE_SIZE
            );
            const newMessages = res.messages.filter(
                (m) => !history.some((existing) => existing.key === m.key)
            );
            if (newMessages.length > 0) {
                const orderedMessages = res.messages.reverse();
                setHistory((prev) => {
                    const prevKeys = new Set(prev.map((m) => m.key));
                    const unique = orderedMessages.filter(
                        (m) => !prevKeys.has(m.key)
                    );
                    return [...prev, ...unique];
                });

                if (isNearBottomRef.current) {
                    requestAnimationFrame(() => {
                        scrollToBottom(true);
                    });
                }
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [history, uuid, brand_id, client_id]);

    const sendMessage = useCallback(() => {
        if (!uuid || !brand_id || !user) return;

        const toSend = message.trim();
        if (toSend.length === 0) return;

        if (toSend.length > MAX_MESSAGE_LENGTH) {
            alert("Message too long. Please keep it under 5000 characters.");
            return;
        }

        const tempMessage: Message = {
            key: `temp-${Date.now()}`,
            message: toSend,
            image: null,
            isUser: false,
            created: new Date(),
            viewed: new Date(),
        };

        setHistory((prev) => [...prev, tempMessage]);
        setMessage("");

        requestAnimationFrame(() => {
            scrollToBottom(false);
        });

        Message.send(uuid, brand_id, user.key, toSend)
            .then(() => loadNewMessages())
            .catch(console.error);
    }, [uuid, brand_id, user, message, loadNewMessages]);

    const sendImage = useCallback(
        async (base64Image: string) => {
            if (!uuid || !brand_id || !user) return;

            const tempKey = `temp-${Date.now()}`;
            const tempMessage: Message = {
                key: tempKey,
                message: "",
                image: base64Image,
                isUser: false,
                created: new Date(),
                viewed: new Date(),
            };

            setHistory((prev) => [...prev, tempMessage]);

            requestAnimationFrame(() => {
                scrollToBottom(false);
            });

            try {
                const stagingUrl = await uploadToCloudinary(base64Image);
                if (!stagingUrl) {
                    console.error("Failed to upload image to Cloudinary.");
                    return;
                }

                await Message.send(
                    uuid,
                    brand_id,
                    user.key,
                    "image",
                    stagingUrl
                );
                await loadNewMessages();
            } catch (error) {
                console.error("Error sending image:", error);
            }
        },
        [uuid, brand_id, user, loadNewMessages]
    );

    const { openFilePicker, loading } = useFilePicker({
        readAs: "DataURL",
        accept: "image/*",
        multiple: false,
        validators: [
            new FileAmountLimitValidator({ max: 1 }),
            new FileTypeValidator(["jpeg", "jpg", "png"]),
            new FileSizeValidator({ maxFileSize: 10 * 1024 * 1024 }),
        ],
        onFilesSelected: ({ errors }) => {
            if (errors) {
                console.error("File upload error:", errors);
            }
        },
        onFilesSuccessfullySelected: ({ filesContent }) => {
            const item = filesContent[0];
            if (item) {
                sendImage(item.content);
            }
        },
    });

    if (user === undefined) return <FullScreenLoader />;

    return (
        <Stack
            height="100vh"
            bgcolor={alpha(primary, 0.02)}
            sx={{ overflow: "hidden" }}
        >
            {/* ── Chat Header ── */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    height: 56,
                    maxWidth: APP_MAX_WIDTH,
                    mx: "auto",
                    px: 1.5,
                    bgcolor: "white",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                }}
            >
                <ButtonBase
                    disabled={loading}
                    onClick={() => navigate("/chats")}
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "12px",
                        bgcolor: alpha(primary, 0.06),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <ArrowBackRounded
                        sx={{ fontSize: 20, color: primary }}
                    />
                </ButtonBase>

                <ButtonBase
                    onClick={() => navigate(`/users/info/${user.key}`)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        borderRadius: "12px",
                        flex: 1,
                        justifyContent: "flex-start",
                    }}
                >
                    {image ? (
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "12px",
                                backgroundImage: `url(${image})`,
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                backgroundSize: "cover",
                                flex: "0 0 auto",
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "12px",
                                bgcolor: alpha(primary, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flex: "0 0 auto",
                            }}
                        >
                            <PersonRounded
                                sx={{
                                    fontSize: 20,
                                    color: primary,
                                    opacity: 0.6,
                                }}
                            />
                        </Box>
                    )}
                    <Typography
                        className="no-translation"
                        fontFamily={FONTS.display}
                        fontWeight={600}
                        fontSize="16px"
                        noWrap
                    >
                        {user.name}
                    </Typography>
                </ButtonBase>
            </Stack>

            {/* ── Messages Area ── */}
            <Box
                ref={chatContainerRef}
                onScroll={(e) => {
                    const el = e.currentTarget;
                    hasScrolledToTopRef.current = el.scrollTop < 100;
                    const nearBottom =
                        el.scrollHeight - el.scrollTop - el.clientHeight < 100;
                    isNearBottomRef.current = nearBottom;
                }}
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    pt: "64px",
                    pb: "80px",
                    px: 2,
                }}
            >
                <Stack
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={1}
                    pt={1}
                >
                    {hasMore ? (
                        loadingOlder && (
                            <CircularProgress size={24} sx={{ my: 2 }} />
                        )
                    ) : (
                        <Stack
                            alignItems="center"
                            spacing={0.75}
                            sx={{ py: 2 }}
                        >
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    bgcolor: alpha(primary, 0.06),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <ChatBubbleOutlineRounded
                                    sx={{
                                        fontSize: 18,
                                        color: primary,
                                        opacity: 0.4,
                                    }}
                                />
                            </Box>
                            <Typography
                                fontSize="12px"
                                color="text.disabled"
                            >
                                Beginning of conversation
                            </Typography>
                        </Stack>
                    )}

                    <div ref={topMessageRef} />

                    {history.map((x) => (
                        <MessageBox
                            key={x.key}
                            uuid={uuid}
                            brand_id={brand_id}
                            user_id={user.key}
                            message={x}
                        />
                    ))}
                    <div
                        ref={messagesEndRef}
                        style={{ marginTop: 8, height: 1 }}
                    />
                </Stack>
            </Box>

            {/* ── Input Bar ── */}
            <Stack
                className="force-dynamic-translation"
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    mx: "auto",
                    maxWidth: APP_MAX_WIDTH,
                    px: 1.5,
                    py: 1,
                    pb: "calc(8px + env(safe-area-inset-bottom))",
                    bgcolor: "white",
                    borderTop: "1px solid rgba(0,0,0,0.06)",
                }}
            >
                <ButtonBase
                    onClick={openFilePicker}
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        bgcolor: alpha(primary, 0.06),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                    }}
                >
                    <AttachFileRounded
                        sx={{
                            fontSize: 20,
                            color: primary,
                            transform: "rotate(45deg)",
                        }}
                    />
                </ButtonBase>

                <Box sx={{ flex: 1 }}>
                    <TextField
                        fullWidth
                        variant="contained"
                        placeholder="Message..."
                        value={message}
                        onChange={(e) => setMessage(e.currentTarget.value)}
                        onKeyDown={(e) => {
                            if (
                                !isMobile &&
                                e.key === "Enter" &&
                                !e.shiftKey
                            ) {
                                e.preventDefault();
                                sendMessage();
                            }
                        }}
                        multiline
                        maxRows={4}
                        sx={{
                            minHeight: "40px",
                            borderRadius: "14px",
                            fontSize: "14px",
                        }}
                    />
                </Box>

                <ButtonBase
                    onClick={sendMessage}
                    disabled={!message.trim()}
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "12px",
                        bgcolor: message.trim()
                            ? primary
                            : alpha(primary, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                        transition:
                            "background-color 0.2s ease, transform 0.15s ease",
                        "&:active": { transform: "scale(0.92)" },
                    }}
                >
                    <SendRounded
                        sx={{
                            fontSize: 18,
                            color: message.trim()
                                ? "white"
                                : alpha(primary, 0.4),
                            transition: "color 0.2s ease",
                        }}
                    />
                </ButtonBase>
            </Stack>
        </Stack>
    );
};

/* ── Message Cloud ── */

const MessageCloud = (props: {
    side: "left" | "right";
    content: string;
    type: "text" | "image";
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    const { side, content, type } = props;

    const borderRadius =
        side === "left" ? "4px 16px 16px 16px" : "16px 16px 4px 16px";
    const bgcolor =
        side === "left" ? alpha(primary, 0.08) : primary;
    const color = side === "left" ? "text.primary" : "white";

    const linkify = (text: string): (string | JSX.Element)[] => {
        const preserveLineBreaks = (
            text: string,
            baseKey: number = 0
        ): JSX.Element[] =>
            text.split("\n").flatMap((line, i, arr) => {
                const spanElement = (
                    <span key={`text-${baseKey}-${i}`}>{line}</span>
                );
                if (i < arr.length - 1) {
                    return [spanElement, <br key={`br-${baseKey}-${i}`} />];
                }
                return [spanElement];
            });

        const urlRegex =
            /\b((https?:\/\/|www\.)[^\s]+|\b[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(:[0-9]+)?(\/[^\s]*)?/gi;

        const elements: (string | JSX.Element)[] = [];
        let lastIndex = 0;
        const linkColor =
            side === "left" ? theme.palette.primary.main : "white";

        for (const match of Array.from(text.matchAll(urlRegex))) {
            const matchText = match[0];
            const matchIndex = match.index ?? 0;

            if (lastIndex < matchIndex) {
                const raw = text.slice(lastIndex, matchIndex);
                elements.push(...preserveLineBreaks(raw, elements.length));
            }

            const href = matchText.startsWith("http")
                ? matchText
                : "https://" + matchText;

            elements.push(
                <a
                    key={`link-${matchIndex}`}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: linkColor, textDecoration: "underline" }}
                >
                    {matchText}
                </a>
            );

            lastIndex = matchIndex + matchText.length;
        }

        if (lastIndex < text.length) {
            const remaining = text.slice(lastIndex);
            elements.push(...preserveLineBreaks(remaining, elements.length));
        }

        return elements;
    };

    if (type === "image")
        return (
            <GalleryImage
                width="180px"
                height="180px"
                image={content}
                sx={{ bgcolor, borderRadius }}
            />
        );

    return (
        <Stack
            sx={{
                px: 1.75,
                py: 1,
                bgcolor,
                borderRadius,
                maxWidth: "88%",
                boxShadow:
                    side === "right"
                        ? `0 1px 4px ${alpha(darken(primary, 0.3), 0.2)}`
                        : "none",
            }}
        >
            <Typography
                // Own messages (right side) stay in the language the sender
                // typed — never run them through Weglot. Received messages are
                // left translatable so cross-language chat still works.
                className={side === "right" ? "no-translation" : undefined}
                fontWeight={400}
                fontSize="14px"
                lineHeight="20px"
                color={color}
                sx={{ wordBreak: "break-word" }}
            >
                {linkify(content)}
            </Typography>
        </Stack>
    );
};

/* ── Message Box ── */

const MessageBox = (props: {
    uuid: string;
    brand_id: string;
    user_id: string;
    message: Message;
}) => {
    const { uuid, brand_id, user_id, message } = props;

    const align = message.isUser ? "flex-start" : "flex-end";
    const side = message.isUser ? "left" : "right";
    const type = message.image ? "image" : "text";
    const content = message.image ?? message.message;

    useEffect(() => {
        // Mark RECEIVED (client) messages as read — not our own outbound ones.
        if (!message.viewed && message.isUser)
            Message.viewed(uuid, brand_id, user_id, message.key).catch(
                console.error
            );
    }, [uuid, brand_id, user_id, message]);

    const time = (time: Dayjs) => {
        const now = dayjs();
        const minutes = now.diff(time, "minutes");
        const hours = now.diff(time, "hours");
        const days = now.diff(time, "days");
        if (days > 1) return time.format("DD.MM.YYYY");
        if (days === 1) return "Yesterday";
        if (hours >= 1) return `${hours}h ago`;
        if (minutes >= 1) return `${minutes}m ago`;
        return "Now";
    };

    return (
        <Stack
            key={message.key}
            width="100%"
            direction="row"
            justifyContent={align}
        >
            <Stack
                direction="column"
                alignItems={align}
                spacing={0.25}
                maxWidth="92%"
            >
                <MessageCloud side={side} content={content} type={type} />
                <Typography
                    fontSize="10px"
                    color="text.disabled"
                    px={0.5}
                >
                    {time(dayjs(message.created))}
                </Typography>
            </Stack>
        </Stack>
    );
};

export default ClientChat;
